import { Modal } from '../../../components/modal/modal';
import { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Spinner } from '../../../components/spinner/spinner';
import { Field, FileField } from '../../../components/field/field';
import {
  DatabaseUserDocumentType,
  User,
  UserDocument,
  UserDocumentType,
  UserDriverLic,
  UserDriverLicRequest,
} from '../../../clients/quipli-service/user/user';
import { DateTime } from 'luxon';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { useAdmin } from '../../../hooks/use-admin-context';
import { SelectForm } from '../../../components/select-form/select-form';
import { Button } from '../../../components/form/button';
import { toast } from '../../../util/toasts';
import { ValidationError } from '../../../components/validation-error/validation-error';
import classNames from 'classnames';
import { InsuranceDocument, InsuranceDocumentRequest } from '../../../clients/customer-api/customer/customer-customer';
import Icon from '../../../components/icons/icon';
import { LegacyDownloadLink } from '../../../components/link/legacy-download-link';
import { StateProvince } from '../../../clients/quipli-service/locales/locales';
import { useLocalesAPI } from '../../../clients/quipli-service/locales/locales-api-v1';
import { LicenseScanFormV1 } from './license-scan-form-v1';
import { LicenseScanFormV2 } from './license-scan-form-v2';

export const UserDocumentForm = ({
  isOpen,
  setIsOpen,
  isEdit,
  userDocument,
  userDriverLicense,
  insuranceDocument,
  customLabel,
  user,
  hideFile = false,
  hideDocumentType = false,
  stateProvinceList,
  onSubmit,
  onMergeUsers,
  existingFile,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isEdit: boolean;
  userDocument?: UserDocument;
  userDriverLicense?: UserDriverLic;
  insuranceDocument?: InsuranceDocument;
  customLabel?: string;
  user: User;
  hideFile?: boolean;
  hideDocumentType?: boolean;
  stateProvinceList?: StateProvince[];
  onSubmit?: () => void;
  onMergeUsers?: (primaryUser: User) => void;
  existingFile?: { documentType: UserDocumentType; fileName?: string };
}) => {
  const {
      createUpdateUserDocument,
      createUserDriverLicense,
      updateUserDriverLicense,
      addInsuranceDocument,
      updateInsuranceDocument,
      mergeUsers,
    } = useAPI(),
    admin = useAdmin();
  const { searchStateProvinces } = useLocalesAPI();
  const userDocumentTypesLbl = [
    {
      id: UserDocumentType.Insurance,
      label: 'Insurance',
      type: DatabaseUserDocumentType.Insurance,
    },
    {
      id: UserDocumentType.License,
      label: 'Drivers License',
      type: DatabaseUserDocumentType.License,
    },
    {
      id: UserDocumentType.OtherCheckout,
      label: customLabel ?? 'Other (Checkout)',
      type: DatabaseUserDocumentType.OtherCheckout,
    },
    {
      id: UserDocumentType.Other,
      label: 'Other',
      type: DatabaseUserDocumentType.Other,
    },
  ];

  const [file, setFile] = useState<File>(),
    [documentType, setDocumentType] = useState<{
      id: string;
      label: string;
      type: number;
    }>(
      userDocument?.documentType
        ? userDocumentTypesLbl.find((type) => type.id == userDocument.documentType)!
        : existingFile
        ? userDocumentTypesLbl.find((type) => type.id == existingFile?.documentType) ??
          (userDriverLicense
            ? userDocumentTypesLbl[1]
            : insuranceDocument
            ? userDocumentTypesLbl[0]
            : userDocumentTypesLbl[2])
        : userDocumentTypesLbl[0],
    );

  // React states
  const [isContentLoading, setContentLoader] = useState<boolean>(false),
    [isRequestInProgress, setRequestInProgress] = useState<boolean>(false),
    [fullName, setFullName] = useState<string>(userDriverLicense ? userDriverLicense.fullName : user.name),
    [driverLicense, setDriverLicense] = useState<string>(userDriverLicense ? userDriverLicense.driverLicNum : ''),
    [expiryDate, setExpiryDate] = useState<DateTime | undefined>(
      userDriverLicense ? userDriverLicense.expirationDate : undefined,
    ),
    [dateOfBirth, setDateOfBirth] = useState<DateTime | undefined>(
      userDriverLicense ? userDriverLicense.dateOfBirth : undefined,
    ),
    [stateProvince, setStateProvince] = useState<StateProvince | undefined>(
      userDriverLicense ? userDriverLicense.state : undefined,
    ),
    [DLFieldsRequired, setDLFieldsRequired] = useState<boolean>(true);
  const [checkDL, setCheckDL] = useState<{
    isValid: boolean;
    error: string;
    owner?: { name: string; id: number; canMerge: boolean };
  }>();
  const [stateProvinces, setStateProvinces] = useState<StateProvince[] | undefined>(stateProvinceList);

  useEffect(() => {
    if (!stateProvinceList) {
      setContentLoader(true);
      searchStateProvinces()
        .then((res) => {
          setStateProvinces(res);
        })
        .catch(() => {
          toast('Failed to load state/province list.', 'error');
        })
        .finally(() => {
          setContentLoader(false);
        });
    } else {
      setStateProvinces(stateProvinceList);
    }
  }, [stateProvinceList]);

  // Callbacks
  const isSetDriveLic = (): boolean => {
      return expiryDate !== undefined && fullName != '' && driverLicense != '';
    },
    isBtnDisabled = (): boolean => {
      if (documentType.id === UserDocumentType.License) {
        return isRequestInProgress || isContentLoading || (!file && !isSetDriveLic());
      } else {
        return isRequestInProgress || isContentLoading || !file;
      }
    },
    closeModal = () => {
      // Don't close modal if there is a request in progress
      if (isRequestInProgress) return;

      setIsOpen(false);
    };

  const addDocument = async () => {
    // Block buttons and exit of modal
    setRequestInProgress(true);

    if (file) {
      try {
        await createUpdateUserDocument(file, user.id.toString(), documentType.type.toString());
        //verify size
      } catch (error) {
        //setError('Something went wrong.');
        //setNewNoteLoading(false);
        return;
      }
    }

    if (documentType.id === UserDocumentType.License && isSetDriveLic()) {
      const body = {
        fullName: fullName,
        driverLicNum: driverLicense,
        expirationDate: expiryDate,
        dateOfBirth: dateOfBirth,
        stateId: stateProvince?.id,
        updatedBy: admin.id,
      };
      try {
        if (userDriverLicense) {
          await updateUserDriverLicense(userDriverLicense.userId, body as UserDriverLicRequest);
        } else {
          const res = await createUserDriverLicense(user.id, {
            ...body,
            skipCheck: checkDL?.isValid === false,
          } as UserDriverLicRequest);

          if (!res.isValid) {
            setCheckDL(res);
            setRequestInProgress(false);
            return;
          }
        }
      } catch (error: any) {
        toast(error.message || 'Something went wrong.', 'error');

        setRequestInProgress(false);
        return;
      }
    }

    // Close modal
    setFile(undefined);
    if (!isEdit) {
      setDriverLicense('');
      setExpiryDate(undefined);
      setDateOfBirth(undefined);
      setStateProvince(undefined);
    }

    setRequestInProgress(false);
    setIsOpen(false);
    onSubmit?.();
  };

  const doMergeUsers = async () => {
    if (checkDL?.isValid || !checkDL?.owner) return;

    setRequestInProgress(true);

    try {
      const mergedUser = await mergeUsers(checkDL.owner.id, user.id);
      onMergeUsers?.(mergedUser);
      setIsOpen(false);
    } catch (error) {
      toast('Error merging users', 'error');
    } finally {
      setRequestInProgress(false);
    }
  };

  useEffect(() => {
    setDLFieldsRequired(!file);
  }, [file]);

  return (
    <Modal
      isOpen={isOpen}
      title={`${isEdit ? 'Update' : 'Add'} document`}
      actions={
        isRequestInProgress ? (
          <Spinner />
        ) : (
          documentType?.id !== UserDocumentType.Insurance && (
            <>
              <Button className="button button-secondary" disabled={isRequestInProgress} onClick={() => closeModal()}>
                Cancel
              </Button>

              {checkDL?.isValid === false && checkDL.owner?.canMerge === true && (
                <Button className="button button-grey" onClick={doMergeUsers}>
                  Merge Users
                </Button>
              )}

              <Button
                className="button button-primary"
                disabled={isBtnDisabled()}
                onClick={() => {
                  addDocument();
                }}
              >
                {`Save${checkDL?.isValid === false ? ' Anyway' : ''}`}
              </Button>
            </>
          )
        )
      }
      close={closeModal}
    >
      {!hideDocumentType && (
        <div className="user-setup-field">
          <SelectForm
            label="Document type"
            menuShouldBlockScroll
            menuPosition="fixed"
            menuPortalTarget={document.body}
            options={userDocumentTypesLbl}
            value={documentType}
            getOptionValue={(option) => option.id}
            onChange={(type) => setDocumentType(type ?? userDocumentTypesLbl[0])}
            isDisabled={isEdit}
          />
        </div>
      )}

      {documentType.id === UserDocumentType.License && (
        <>
          <Field
            type="text"
            label="Full Name"
            value={fullName}
            onChange={(name: string) => {
              setFullName(name);
            }}
            isRequired={DLFieldsRequired}
          />
          <DatePicker label="Date of Birth" value={dateOfBirth} onChange={setDateOfBirth} />
          <Field
            type="text"
            label="License Number"
            value={driverLicense}
            onChange={(license: string) => {
              setDriverLicense(license);
            }}
            isRequired={DLFieldsRequired}
          />
          <SelectForm
            label="Issuing State/Province"
            menuShouldBlockScroll
            menuPosition="fixed"
            menuPortalTarget={document.body}
            options={stateProvinces}
            value={stateProvince}
            onChange={(option) => {
              setStateProvince(option ?? undefined);
            }}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => `${o.id}`}
          />
          <DatePicker
            label="Expiration Date"
            isRequired={DLFieldsRequired}
            value={expiryDate}
            onChange={setExpiryDate}
          />
          <ValidationError className={classNames({ hidden: checkDL?.isValid !== false })}>
            <span>{checkDL?.error}</span>
          </ValidationError>
        </>
      )}

      {documentType.id === UserDocumentType.Insurance && (
        <InsuranceForm
          insuranceDocument={insuranceDocument}
          userId={user.id}
          closeModal={closeModal}
          addInsuranceDocument={addInsuranceDocument}
          updateInsuranceDocument={updateInsuranceDocument}
          onSubmit={onSubmit}
        />
      )}

      {!hideFile && documentType.id !== UserDocumentType.Insurance && (
        <>
          <FileField onChange={(file) => setFile(file as File)} value={file} />

          {existingFile?.fileName && (
            <LegacyDownloadLink
              className="w-4/5 flex justify-between"
              href={`users/download_file/${user.id}/${DatabaseUserDocumentType[existingFile.documentType]}`}
            >
              <span className="text-document-overflow">{existingFile.fileName}</span>
              <Icon icon="Download" size="medium" />
            </LegacyDownloadLink>
          )}
        </>
      )}
    </Modal>
  );
};

export const InsuranceForm = ({
  insuranceDocument,
  userId,
  hideFile,
  closeModal,
  addInsuranceDocument,
  updateInsuranceDocument,
  onSubmit,
  setRootAttributes,
}: {
  insuranceDocument?: InsuranceDocument;
  userId: number;
  hideFile?: boolean;
  closeModal?: () => void;
  addInsuranceDocument: (userId: number, body: InsuranceDocumentRequest) => Promise<InsuranceDocument | undefined>;
  updateInsuranceDocument: (
    userId: number,
    documentId: number,
    body: InsuranceDocumentRequest,
  ) => Promise<InsuranceDocument | undefined>;
  onSubmit?: () => void;
  setRootAttributes?: (o: any) => void;
}) => {
  const { createUpdateUserDocument } = useAPI();
  const [file, setFile] = useState<File>();
  // React states
  const [isRequestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [insuranceState, setInsuranceState] = useState<{
    firstName?: string;
    lastName?: string;
    issuedBy?: string;
    policyNumber?: string;
    coverageAmount?: number;
    file?: UserDocument;
  }>(
    insuranceDocument ?? {
      firstName: '',
      lastName: '',
      issuedBy: '',
      policyNumber: '',
      coverageAmount: undefined,
      file: undefined,
    },
  );
  const [expiryDate, setExpiryDate] = useState<DateTime | undefined>(insuranceDocument?.expiryDate);
  const [issuedDate, setIssuedDate] = useState<DateTime | undefined>(insuranceDocument?.issuedDate);

  const addDocument = async () => {
    // Block buttons and exit of modal
    setRequestInProgress(true);
    const isEdit =
      !!insuranceDocument?.id &&
      insuranceDocument.issuedBy === insuranceState.issuedBy &&
      insuranceDocument.policyNumber === insuranceState.policyNumber &&
      insuranceDocument.expiryDate!.toISODate() === expiryDate?.toISODate() &&
      insuranceDocument.issuedDate!.toISODate() === issuedDate?.toISODate() &&
      insuranceDocument.coverageAmount === insuranceState.coverageAmount;
    let newFileId;
    if (file) {
      try {
        const newFile = await createUpdateUserDocument(file, userId.toString(), UserDocumentType.Insurance, isEdit);
        newFileId = newFile?.fileId;
        //verify size
        if (setRootAttributes) {
          setRootAttributes({
            'data-saved': `true`,
          });
        }
      } catch (error) {
        toast('Error uploading file', 'error');
        return;
      }
    }
    if (
      insuranceState.firstName &&
      insuranceState.lastName &&
      insuranceState.issuedBy &&
      insuranceState.policyNumber &&
      expiryDate &&
      issuedDate
    ) {
      try {
        const body: InsuranceDocumentRequest = {
          userId: userId,
          firstName: insuranceState.firstName,
          lastName: insuranceState.lastName,
          issuedBy: insuranceState.issuedBy,
          policyNumber: insuranceState.policyNumber,
          expiryDate: expiryDate,
          issuedDate: issuedDate,
          coverageAmount: insuranceState.coverageAmount,
          userFileId: newFileId ?? insuranceDocument?.file?.id,
        };
        if (isEdit && insuranceDocument?.id) {
          await updateInsuranceDocument(userId, insuranceDocument!.id, body);
          if (setRootAttributes) {
            setRootAttributes({
              'data-saved': `true`,
            });
          }
        } else if (body.expiryDate) {
          await addInsuranceDocument(userId, body);
          if (setRootAttributes) {
            setRootAttributes({
              'data-saved': `true`,
            });
          }
        }
        closeModal && closeModal();
      } catch (error) {
        console.error(error);
        toast('Error saving insurance document', 'error');
        setRequestInProgress(false);
        if (setRootAttributes) {
          setRootAttributes({
            'data-saved': `false`,
          });
        }

        return;
      } finally {
        setRequestInProgress(false);
      }
    } else {
      setRequestInProgress(false);
      closeModal && closeModal();
    }
    onSubmit?.();
  };

  const isButtonDisabled = () => {
    return (
      isRequestInProgress ||
      (file &&
        (insuranceState.firstName ||
          insuranceState.lastName ||
          insuranceState.issuedBy ||
          insuranceState.policyNumber ||
          expiryDate ||
          issuedDate) &&
        !(
          insuranceState.firstName &&
          insuranceState.lastName &&
          insuranceState.issuedBy &&
          insuranceState.policyNumber &&
          expiryDate &&
          issuedDate
        )) ||
      (!file &&
        (!insuranceState.firstName ||
          !insuranceState.lastName ||
          !insuranceState.issuedBy ||
          !insuranceState.policyNumber ||
          !expiryDate ||
          !issuedDate))
    );
  };

  useEffect(() => {
    if (insuranceDocument) {
      setInsuranceState(insuranceDocument);
    }
  }, [insuranceDocument]);
  return (
    <div className="w-full insurance-form">
      <div className="flex gap-2">
        <Field
          type="text"
          isRequired={!file}
          label="First Name"
          value={insuranceState.firstName}
          onChange={(firstName: string) => setInsuranceState({ ...insuranceState, firstName })}
        />
        <Field
          type="text"
          isRequired={!file}
          label="Last Name"
          value={insuranceState.lastName}
          onChange={(lastName: string) => setInsuranceState({ ...insuranceState, lastName })}
        />
      </div>
      <Field
        className="my-2"
        type="text"
        isRequired={!file}
        label="Issued By"
        value={insuranceState.issuedBy}
        onChange={(issuedBy: string) => setInsuranceState({ ...insuranceState, issuedBy })}
      />
      <Field
        className="mb-2"
        type="text"
        isRequired={!file}
        label="Policy Number"
        value={insuranceState.policyNumber}
        onChange={(policyNumber: string) => setInsuranceState({ ...insuranceState, policyNumber })}
      />
      <DatePicker
        label="Expiry Date"
        isRequired={!file}
        value={expiryDate}
        onChange={(expiryDate) => setExpiryDate(expiryDate)}
      />
      <DatePicker
        isRequired={!file}
        label="Issued Date"
        value={issuedDate}
        onChange={(issuedDate) => setIssuedDate(issuedDate)}
      />
      <Field
        className="mb-2"
        type="currency"
        label="Coverage Amount"
        value={insuranceState.coverageAmount}
        onChange={(coverageAmount: number) => setInsuranceState({ ...insuranceState, coverageAmount })}
      />

      {!hideFile && (
        <>
          <FileField
            className="w-full mb-2"
            label="Upload file"
            value={file}
            onChange={(newFile) => setFile(newFile as File)}
          />
          {insuranceDocument?.file && (
            <LegacyDownloadLink
              className="w-4/5 flex justify-between mt-2"
              href={`users/download_file/${userId}/${DatabaseUserDocumentType[insuranceDocument.file.documentType]}`}
            >
              <span className="text-document-overflow">{insuranceDocument.file.fileName}</span>
              <Icon icon="Download" size="medium" />
            </LegacyDownloadLink>
          )}{' '}
        </>
      )}

      <div className="flex justify-between mt-4 gap-2">
        {closeModal && (
          <Button className="w-full button-secondary" disabled={isRequestInProgress} onClick={() => closeModal()}>
            Cancel
          </Button>
        )}

        <Button
          id="save-insurance-button"
          className="w-full button-primary"
          disabled={isButtonDisabled()}
          onClick={() => {
            addDocument();
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export const LicenseScanModal = ({
  user,
  isOpen,
  setIsOpen,
  onSuccess,
  licenseScanProfileVersion = 1,
}: {
  user: User;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccess: () => void;
  licenseScanProfileVersion?: number;
}) => {
  const onUserScanSuccessful = () => {
    onSuccess();
    setIsOpen(false);
  };

  return (
    <Modal isOpen={isOpen} title={'Scan Driver License'}>
      {licenseScanProfileVersion === 2 ? (
        <LicenseScanFormV2
          userScanSuccessful={onUserScanSuccessful}
          userScanFailed={() => setIsOpen(false)}
          closeModal={() => setIsOpen(false)}
          user={user}
        />
      ) : (
        <LicenseScanFormV1
          userScanSuccessful={onUserScanSuccessful}
          userScanFailed={() => setIsOpen(false)}
          closeModal={() => setIsOpen(false)}
          user={user}
        />
      )}
    </Modal>
  );
};
